// Única salida del navegador hacia el servidor del Platform Admin nuevo: mismo origen, cookie HttpOnly y, en cada
// escritura, el token antiforgery que entrega GET /api/admin/session. Nunca habla con un microservicio.

export type Session = {
  authenticated: boolean;
  displayName: string | null;
  email: string | null;
  antiforgeryToken: string;
  /** Dirección del Platform Admin actual: desde R7 ninguna pantalla lleva allí (G5); el servidor aún la informa. */
  legacyAdminUrl: string;
  /** Rol de plataforma (PLATFORM_ADMIN, PLATFORM_SELLER) para el menú de la cuenta. */
  role?: string | null;
  /** Identity le dio permiso de lectura del Error Hub: solo así se muestra esa área (el servidor igual lo exige). */
  canErrorHub?: boolean;
  userId?: number;
};

let antiforgeryToken: string | null = null;

/** Respuesta de una API con su código HTTP: las pantallas deciden qué mostrar con 400, 403, 404 o 502. */
export type Reply<T> = { status: number; body: T | null };

export async function loadSession(): Promise<Session> {
  const response = await fetch("/api/admin/session", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  const session = (await response.json()) as Session;
  antiforgeryToken = session.antiforgeryToken;
  return session;
}

async function request<T>(method: string, url: string, body?: unknown, retried = false): Promise<Reply<T>> {
  if (method !== "GET" && !antiforgeryToken) await loadSession();
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(method === "GET" ? {} : { "X-CSRF-TOKEN": antiforgeryToken ?? "" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  // Sin sesión: al ingreso, volviendo después a esta misma pantalla. El ingreso y el cambio de contraseña responden 401
  // por credenciales incorrectas: esa respuesta la lee su pantalla.
  if (response.status === 401 && !url.startsWith("/api/admin/session")) {
    const back = window.location.pathname + window.location.search;
    window.location.assign(`/login?error=session-ended&ReturnUrl=${encodeURIComponent(back)}`);
    return { status: 401, body: null };
  }
  // Token antiforgery de otra sesión (se volvió a ingresar en otra pestaña): se pide uno nuevo y se reintenta una vez.
  if (response.status === 400 && method !== "GET" && !retried) {
    const peek = await response
      .clone()
      .json()
      .catch(() => null);
    if (peek && (peek.code === "PAGE_EXPIRED" || peek.code === "ANTIFORGERY")) {
      await loadSession();
      return request<T>(method, url, body, true);
    }
  }
  const text = await response.text();
  let parsed: T | null = null;
  try {
    parsed = text ? (JSON.parse(text) as T) : null;
  } catch {
    parsed = null;
  }
  return { status: response.status, body: parsed };
}

export const getJson = <T>(url: string) => request<T>("GET", url);
export const postJson = <T>(url: string, body: unknown) => request<T>("POST", url, body);

/** Copia al portapapeles; false si el navegador no lo permite (la pantalla lo dice en un toast). */
export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}
